import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError, fetchMyContacts, sendContact } from '../api/client';
import type { ContactCategory, MyContact } from '../api/types';
import { useAuth } from '../auth/AuthProvider';

/**
 * 問い合わせ（`/contact`。doc/AUTH.md §14）。**ログインした人だけ**が送れる。
 *
 * メールアドレスを画面に貼り出さず、フォームで受ける。誰からかは `uid` で分かるので氏名は訊かない。
 * 返信先は任意（書かなければ持たない）。結果について訊くときは、結果ページの URL を添えてもらう
 * — `?result=` を付けて開くと、その欄が埋まった状態で始まる（結果ページからの導線）。
 *
 * ログインを求めるのは、荒らしの歯止め（1 人 1 日 5 通）と、
 * 「自分の診断・自分のアカウントについて」の問い合わせに本人確認が要るため。
 */
const CATEGORIES: readonly { readonly id: ContactCategory; readonly label: string }[] = [
  { id: 'result', label: '診断の結果について' },
  { id: 'theme', label: '作った診断・キャラクターの絵について' },
  { id: 'account', label: 'ログイン・アカウントについて' },
  { id: 'ads', label: '広告・掲載のご相談' },
  { id: 'delete', label: 'データの削除・開示の依頼' },
  { id: 'other', label: 'その他' },
];

const CATEGORY_LABEL = new Map(CATEGORIES.map((c) => [c.id, c.label]));

export function ContactPage() {
  const { ready, user, email, enabled, busy, requestSignIn } = useAuth();
  const [params] = useSearchParams();
  const [category, setCategory] = useState<ContactCategory>((params.get('about') as ContactCategory) ?? 'other');
  const [message, setMessage] = useState('');
  const [result, setResult] = useState(params.get('result') ?? '');
  const [replyTo, setReplyTo] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sentId, setSentId] = useState('');
  const [history, setHistory] = useState<readonly MyContact[]>([]);

  // 返信先は、ログインに使っているアドレスを初めに入れておく（消して送ってもよい）
  useEffect(() => { if (email) setReplyTo((current) => current || email); }, [email]);

  useEffect(() => {
    if (!user) { setHistory([]); return; }
    void fetchMyContacts().then((response) => setHistory(response.contacts)).catch(() => undefined);
  }, [user, sentId]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setSending(true);
    try {
      if (!user && !(await requestSignIn())) return;
      const sent = await sendContact({ category, message, result: result.trim(), replyTo: replyTo.trim() });
      setSentId(sent.contactId);
      setMessage('');
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : '送れませんでした。時間をおいてもう一度お試しください。');
    } finally {
      setSending(false);
    }
  };

  if (!ready) return <p className="page__notice">読み込んでいます…</p>;

  return (
    <main className="page">
      <h1 className="page__title">お問い合わせ</h1>
      <p className="page__lead">
        ログインしてからお送りください。どなたからの問い合わせかが分かるようにするためと、自分の診断や登録についてのご依頼を、ご本人からのものとして扱うためです。よくある質問は <Link to="/help">ヘルプ</Link> にまとめています。
      </p>

      {!user && (
        <section className="section">
          <p className="page__lead">お問い合わせにはログインが必要です。</p>
          <button className="button button--primary" type="button" onClick={() => void requestSignIn()} disabled={busy || !enabled}>
            {enabled ? 'ログインする' : 'ログイン準備中'}
          </button>
        </section>
      )}

      {sentId && (
        <p className="modal__notice" role="status">
          お送りしました（受付番号 {sentId.slice(0, 8)}）。返信先を書いていただいた場合は、そちらへお返事します。
        </p>
      )}

      <form className="panel" onSubmit={(e) => void submit(e)}>
        <label className="field">
          <span className="field__label">種類</span>
          <select className="field__input" value={category} onChange={(e) => setCategory(e.target.value as ContactCategory)} disabled={sending}>
            {CATEGORIES.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
          </select>
        </label>

        <label className="field">
          <span className="field__label">内容（10 文字以上・2000 文字まで）</span>
          <textarea className="field__input" value={message} rows={8} maxLength={2000} required disabled={sending}
            placeholder="例：この結果を消してほしいです。結果ページの URL は下に貼りました。"
            onChange={(e) => setMessage(e.target.value)} />
        </label>

        <label className="field">
          <span className="field__label">関連する結果ページの URL（任意）</span>
          <input className="field__input" value={result} maxLength={200} disabled={sending}
            placeholder="https://…/r/XXXXXXXX" onChange={(e) => setResult(e.target.value)} />
        </label>

        <label className="field">
          <span className="field__label">返信先のメールアドレス（任意）</span>
          <input className="field__input" type="email" value={replyTo} maxLength={254} disabled={sending}
            placeholder="you@example.co.jp" onChange={(e) => setReplyTo(e.target.value)} />
        </label>
        <p className="page__footnote">
          返信先は<strong>返信のためだけ</strong>に使い、対応が済んだら消します。空のままでも送れます（そのときはお返事ができません）。ふだんは氏名もメールアドレスも保存していません。くわしくは <Link to="/privacy">プライバシーポリシー</Link> をご覧ください。
        </p>

        {error && <p className="modal__error" role="alert">{error}</p>}

        <div className="page__actions">
          <button className="button button--primary" type="submit" disabled={sending || !enabled}>
            {sending ? '送っています…' : user ? '送る' : 'ログインして送る'}
          </button>
          <Link className="button button--ghost" to="/help">ヘルプを見る</Link>
        </div>
      </form>

      {history.length > 0 && (
        <section className="section">
          <h2 className="section__title">送ったお問い合わせ</h2>
          <ul className="contact-history">
            {history.map((item) => (
              <li key={item.contactId} className="contact-history__item">
                <p className="contact-history__head">
                  <span className="chip">{CATEGORY_LABEL.get(item.category) ?? item.category}</span>
                  <span className="contact-history__at">{new Date(item.createdAt).toLocaleString('ja-JP')}</span>
                  <span className={`badge ${item.status === 'done' ? 'badge--done' : ''}`}>
                    {item.status === 'done' ? '対応済み' : '受付'}
                  </span>
                </p>
                <p className="contact-history__body">{item.message}</p>
              </li>
            ))}
          </ul>
        </section>
      )}
    </main>
  );
}
